import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { filter, take } from "rxjs/operators";
import { SubSink } from "subsink";
import { UserToken } from "../../../../../../../auth/user-token.model";
import { MessagingService } from "../../../../../../../core/messaging/messaging.service";
import { SeverityType } from "../../../../../../../core/messaging/severity-type.enum";
import { LocalService } from "../../../../../../../core/storage/local.service";
import { UserService } from "../../../../../../../core/user/user.service";
import { GridRequest } from "../../../../../../../shared/grid/models/grid-request.model";
import { ListItem } from "../../../../../../../shared/list/list-item";
import { PendStatus } from "../../../../../../../shared/pend/pend-status.enum";
import { ArrayHelper } from "../../../../../../../utilities/contracts/array-helper";
import { NumberHelper } from "../../../../../../../utilities/contracts/number-helper";
import { EntityError } from "../../../../../../api/member-validation/entity-error.model";
import { WorkflowStatusDb } from "../../../../../../api/workflow/workflow-status-db.enum";
import { FunctionalRole } from "../../../../../dashboard/retrieval/functional-role.enum";
import { DASHBOARD_MY_CHASES_GRID } from "../../../../chase-detail/chase-detail-chart/attributes";
import { ChartService } from "../../../../chase-detail/chase-detail-chart/chart.service";
import { RiskError } from "../../../../chase-detail/chase-detail-chart/risk/risk-error-modal/risk-error.model";
import { RiskState } from "../../../../chase-detail/chase-detail-chart/risk/risk-state.model";
import { RiskService } from "../../../../chase-detail/chase-detail-chart/risk/risk.service";
import { ChaseDetailState } from "../../../../chase-detail/chase-detail-state.model";
import { ChaseDetailStateService } from "../../../../chase-detail/chase-detail-state.service";
import { ChaseDetailService } from "../../../../chase-detail/chase-detail.service";
import { ChaseDetailV2ChartRiskService } from "../risk.service";
import { ChaseQueueService } from "../shared/chase-queue.service";

@Component({
  selector: "member-risk-submit",
  templateUrl: "./risk-submit.component.html",
  styleUrls: ["./risk-submit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RiskSubmitComponent implements OnInit, OnDestroy {
  private sink = new SubSink();
  chaseDetails: ListItem[] = [];
  chaseDetailState: ChaseDetailState;
  chaseId: number;
  formGroup = new FormGroup({});
  serverRequest = new GridRequest();
  isResolvePendWarningVisible = false;
  user: UserToken;
  isReadOnly = false;
  errorsChecked = false;
  private openChaseAndUserInfo = "openChaseAndUserInfo";
  stateName = DASHBOARD_MY_CHASES_GRID.attributeCode;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly chartService: ChartService,
    private readonly chaseDetailService: ChaseDetailService,
    private readonly chaseDetailStateService: ChaseDetailStateService,
    private readonly localService: LocalService,
    private readonly messagingService: MessagingService,
    private readonly riskService: RiskService,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly chaseDetailV2ChartRiskService: ChaseDetailV2ChartRiskService,
    private readonly chaseQueueService: ChaseQueueService
  ) { }

  get status(): string {
    const statusItem = this.chaseDetails.find(item => item.key === "Workflow Status");
    return statusItem ? statusItem.value : "";
  }

  get hasUnresolvedPend(): boolean {
    return this.chaseDetailState.hasPend &&
      (this.chaseDetailState.pendStatusId === PendStatus.New || this.chaseDetailState.pendStatusId === PendStatus.InProgress);
  }

  get isOverread(): boolean {
    return this.chaseDetailState.workflowStatus === WorkflowStatusDb.Overread;
  }

  get isOverread2(): boolean {
    return this.chaseDetailState.workflowStatus === WorkflowStatusDb.Overread2;
  }

  get functionalRoleId(): number {
    switch (this.chaseDetailState.workflowStatus) {
      case WorkflowStatusDb.Abstraction:
        return FunctionalRole.ABSTRACTOR;
      case WorkflowStatusDb.Overread:
        return FunctionalRole.OVERREAD;
      case WorkflowStatusDb.Overread2:
        return FunctionalRole.CLIENTOVERREAD;
      default:
        return 0;
    }
  }

  ngOnInit(): void {
    this.user = this.userService.getUserToken();
    this.sink.add(
      this.riskService.data
        .pipe(
          filter(riskState => NumberHelper.isGreaterThan(riskState.encounterCount, 0)),
          take(1)
        )
        .subscribe(riskState => this.enableSubmitTab(riskState)),
      this.activatedRoute.parent.paramMap.subscribe(params => {
        this.chaseId = NumberHelper.isGreaterThan(+params.get("chaseGd"), 0) ? +params.get("chaseGd") : 0;
      }),
      this.chaseDetailService.chaseDetailsChangeObserver.subscribe(chaseDetails => {
        this.chaseDetails = chaseDetails;
        // this.getMemberValidation();
      }),
      this.chaseDetailStateService.state.subscribe(state => {
        this.chaseDetailState = state;
        // this.riskService.setData({ assignedToCurrentUser: this.assignedToCurrentUser });
        this.changeDetector.markForCheck();
      }),
      this.riskService.data.subscribe(riskState => {
        if (ArrayHelper.isAvailable(riskState.errors) && !this.errorsChecked && NumberHelper.isGreaterThan(riskState.errors.length, 1)) {
          this.submit();
        } else if (ArrayHelper.isAvailable(riskState.errors) && !this.errorsChecked && riskState.errors.length === 1) {
          this.riskService.setData({ errors: [] });
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.sink.unsubscribe();
  }

  submit(): void {

    if (this.isReadOnly) {
      return;
    }

    if (this.hasUnresolvedPend && (this.isOverread || this.isOverread2)) {
      this.isResolvePendWarningVisible = true;
      return;
    }

    this.submitAndRouteToDashboard();

    // TODO: We should add formgroup validation in the encounter tab section.
    // const valid = this.getValidation();

    // if (valid) {
    //   if (this.formGroup.valid) {
    //     this.submitAndRouteToDashboard();
    //   } else {
    //     this.markAllAsDirtyAndTouched(this.formGroup);
    //     this.formService.updateDom.next();
    //   }
    // } else {
    //   this.messagingService.showToast("Please complete the checklist.", SeverityType.ERROR);
    // }
  }

  updateChaseData() {
    this.sink.add(
      this.chaseDetailService.getSummaryItems(this.chaseId)
        .subscribe(items => {
          this.chaseDetailService.chaseDetailsChange.next(items);
          this.setPendDetails(items);
        })
    );
  }

  setPendDetails(items: ListItem[]): void {
    items.forEach(x => {
      switch (x.key) {
        case "PendStatusId":
          this.chaseDetailStateService.setData({
            pendStatusId: Number(x.value),
            hasPend: NumberHelper.isGreaterThan(Number(x.value), 0),
          });
          break;
        case "Pend Code":
          this.chaseDetailStateService.setData({
            pendCode: x.value,
          });
          break;
        case "PendCodeName":
          this.chaseDetailStateService.setData({
            pendName: x.value,
          });
          break;
        case "ChasePendId":
          this.chaseDetailStateService.setData({
            chasePendId: Number(x.value),
          });
          break;
        case "PendStatusName":
            this.chaseDetailStateService.setData({
              pendStatusName: x.value,
            });
            break;
        case "Status":
          this.chaseDetailStateService.setData({ reportingStatusName: x.value });
          break;
        default:
          break;
      }
    });
  }

  private submitAndRouteToDashboard(): void {

    this.chartService.submit2(this.chaseId, this.status).subscribe(entityErrors => {
      this.errorsChecked = true;
      if (ArrayHelper.isAvailable(entityErrors)) {
        const errors = entityErrors.map(this.getRiskError.bind(this)) as RiskError[];
        this.riskService.setData({ errors });
      } else {
        this.messagingService.showToast("Submission Succeeded!", SeverityType.SUCCESS);
        this.removeOpenChaseInfo();
        this.chaseQueueService.getQueueOrNextChase(this.user.userId, this.functionalRoleId, this.chaseDetailState);
      }
    });
  }

  private removeOpenChaseInfo(): void {
    const localChaseUserInfo =  this.localService.get(this.openChaseAndUserInfo, null);
    if (localChaseUserInfo) {
      this.localService.delete(this.openChaseAndUserInfo);
    }
  }

  private enableSubmitTab(riskState: RiskState): void {
    if (!riskState.assignedToCurrentUser) {
      this.isReadOnly = true;
      return;
    }

    const enable = this.chaseDetailV2ChartRiskService.isSubmitEnabled(riskState);
    if (!enable) {
      this.router.navigate(["../"], { relativeTo: this.activatedRoute });
    }
  }

  private getRiskError(entityError: EntityError): RiskError {
    const entity = this.riskService.data.value.encounters.reduce(
      (acc, encounter) => {
        if (acc != null) {
          return acc;
        } else if (encounter.entityId === entityError.entityId) {
          return encounter;
        } else {
          return encounter.diagnoses.find(diagnosis => diagnosis.entityId === entityError.entityId);
        }
      },
      null);

    const riskError = new RiskError({
      entityId: entity?.entityId,
      parentEntityId: entity?.parentEntityId,
      message: entityError.validationMessage,
    });

    return riskError;
  }

  private markAllAsDirtyAndTouched = (control: AbstractControl): void => {
    control.markAsDirty();
    control.markAsTouched();

    const controls = (control as any).controls || {};
    const keys = Object.keys(controls);
    if (ArrayHelper.isAvailable(keys)) {
      keys.forEach(key => this.markAllAsDirtyAndTouched(controls[key]));
    }
  }
}
